import { Box, Button, Typography } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import LogoLoader from "../../common/LogoLoader";
import { useAuth } from "../../../contexts/AuthContext";
import { useTreemap } from "../../../hooks/queries/useTreemap";
import { monospaceStyle } from "../../../theme";
import { Treemap } from "../../../types/analytics";
import { useCapturing } from "../../../utils/export/ExportStage";
import { rememberedHome } from "../../ValueChain/homeMemory";
import { AnalyticsPaneProps } from "../analyticsPanes";
import {
  ANALYTICS_VIEW_LABELS,
  TreemapSettings,
  selectionOf,
  toTreemapParams,
} from "../analyticsState";
import TreemapCanvas from "./TreemapCanvas";
import TreemapLegend from "./TreemapLegend";
import TreemapPanel, { OWN_PREFIX } from "./TreemapPanel";
import { gapsBeneath, indexTreemap, layoutTreemap, withoutGaps } from "./treemapLayout";
import { classLabel, counted } from "./treemapModel";

// The height the canvas asks for and the least it takes; the size it is
// laid out at where there is none to measure
export const CANVAS_HEIGHT = 640;
export const CANVAS_MIN_HEIGHT = 520;
export const FALLBACK_WIDTH = 800;

// The floors in force, the gap classes left out and the classes too small to
// draw, as a caption. `undrawn` is how many classes with nothing on record
// the view leaves out; null where it draws them.
export const floorsCaption = (
  data: Treemap,
  omittedGaps: number,
  hidden = 0,
  undrawn: number | null = null,
): string =>
  [
    `a class needs ${counted(data.floors.min_support, "product")} to count firmly`,
    ...(undrawn === null
      ? [
          `at most ${data.floors.top_n} gap classes drawn under a class`,
          ...(omittedGaps > 0
            ? [`${omittedGaps} gap ${omittedGaps === 1 ? "class" : "classes"} not drawn here`]
            : []),
        ]
      : undrawn > 0
        ? [`${undrawn} ${undrawn === 1 ? "class" : "classes"} with nothing on record not drawn`]
        : []),
    ...(hidden > 0
      ? [`${hidden} ${hidden === 1 ? "class" : "classes"} too small to draw here: drill in to see ${hidden === 1 ? "it" : "them"}`]
      : []),
    ...(data.thin_scope
      ? [`the scope holds under ${counted(data.floors.min_scope, "product")}: every number is a hint`]
      : []),
  ].join(" · ");

// A crumb of the drill path: a quiet link, plain where it is the place
const CRUMB_SX = {
  p: 0,
  minWidth: 0,
  fontSize: 12,
  fontWeight: 400,
  lineHeight: 1.5,
  textTransform: "none",
  color: "link.main",
  "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
  '&[aria-current="location"]': { color: "text.primary", fontWeight: 500 },
} as const;

// The treemap: the classes of the scope as nested rectangles, sized by a
// measure and coloured by another reading, drilled into one class at a time:
// a click on a class shows that class alone, its classes drawn larger, and
// the path above the canvas leads back. A class with nothing beneath it
// opens in the overlay instead.
const TreemapView: React.FC<AnalyticsPaneProps> = ({ state, onChange, onOpenEntity }) => {
  const settings = state.settings.treemap;
  const userId = useAuth().user?.id ?? null;
  const params = useMemo(
    () => toTreemapParams(state, rememberedHome(userId)),
    [state, userId],
  );
  const read = useTreemap(params);
  const data: Treemap | null = read.data ?? null;

  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // The size of the canvas: that of the surface around it, which nothing
  // inside it stretches; a fallback where there is none to measure
  const surface = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: FALLBACK_WIDTH, height: CANVAS_HEIGHT });
  useLayoutEffect(() => {
    const element = surface.current;
    if (!element) {
      return undefined;
    }
    const measure = () => {
      const width = element.clientWidth > 0 ? element.clientWidth : FALLBACK_WIDTH;
      const height = element.clientHeight > 0 ? element.clientHeight : CANVAS_HEIGHT;
      setSize((current) =>
        current.width === width && current.height === height ? current : { width, height },
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const index = useMemo(() => (data ? indexTreemap(data) : null), [data]);
  // The classes with nothing on record are drawn when asked for, and inside
  // one of them, where there is nothing else to draw. The panel reads the
  // whole response either way: it names them beneath a class.
  const gapsDrawn =
    settings.gaps || Boolean(settings.root && index?.byId.get(settings.root)?.gap);
  const layout = useMemo(
    () =>
      data
        ? layoutTreemap(
            gapsDrawn ? data : withoutGaps(data),
            settings.root,
            settings.measure,
            size.width,
            size.height,
          )
        : null,
    [data, gapsDrawn, settings.root, settings.measure, size],
  );

  // A new response may no longer hold what was selected
  useEffect(() => {
    if (!index || !selectedKey) {
      return;
    }
    const id = selectedKey.startsWith(OWN_PREFIX)
      ? selectedKey.slice(OWN_PREFIX.length)
      : selectedKey;
    if (!index.byId.has(id)) {
      setSelectedKey(null);
    }
  }, [index, selectedKey]);

  const setSettings = useCallback(
    (next: TreemapSettings) => {
      onChange({ settings: { ...state.settings, treemap: next } });
    },
    [onChange, state.settings],
  );

  const drill = useCallback(
    (root: string | null) => {
      setSelectedKey(null);
      setSettings({ ...settings, root });
    },
    [setSettings, settings],
  );

  // A class with nothing beneath it opens in the overlay straight away
  const open = useCallback(
    (nodeId: string) => {
      const node = index?.byId.get(nodeId);
      if (node) {
        onOpenEntity(node.category.id, node.category.type);
      }
    },
    [index, onOpenEntity],
  );

  const noClass =
    data !== null && !data.nodes.some((node) => !node.other && !node.gap);
  const path = layout?.path ?? [];
  const omittedGaps = data
    ? path.length > 0
      ? path[path.length - 1].omitted_gaps
      : data.omitted_root_gaps
    : 0;
  const undrawnGaps =
    data && index && !gapsDrawn
      ? gapsBeneath(data, index, path.length > 0 ? path[path.length - 1] : null)
      : null;
  const mapped = data !== null && layout !== null && index !== null && !noClass;
  // In an exported image: the drill path, which says where the map is, but
  // not the button to the details, nor the panel
  const capturing = useCapturing();

  return (
    <Box
      component="section"
      aria-label={ANALYTICS_VIEW_LABELS.treemap}
      sx={{
        flex: "1 1 auto",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: "14px",
      }}
    >
      {mapped && data && (
        <Box
          component="nav"
          aria-label="Drill path"
          sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}
        >
          <Button
            disableRipple
            onClick={() => drill(null)}
            aria-label="All"
            aria-current={path.length === 0 ? "location" : undefined}
            sx={CRUMB_SX}
          >
            All ·&nbsp;
            <Box component="span" sx={monospaceStyle}>
              {data.evidence.n}
            </Box>
          </Button>
          {path.map((node, position) => (
            <React.Fragment key={node.id}>
              <Typography component="span" color="text.disabled" aria-hidden sx={{ fontSize: 12 }}>
                ›
              </Typography>
              <Button
                disableRipple
                onClick={() => drill(node.id)}
                aria-label={classLabel(node)}
                aria-current={position === path.length - 1 ? "location" : undefined}
                sx={CRUMB_SX}
              >
                {classLabel(node)}
                {position === path.length - 1 && (
                  <>
                    &nbsp;·&nbsp;
                    <Box component="span" sx={monospaceStyle}>
                      {node.product_count}
                    </Box>
                  </>
                )}
              </Button>
            </React.Fragment>
          ))}
          {/* A click on the class drilled into it: its numbers are read here */}
          {path.length > 0 && !capturing && (
            <Button
              disableRipple
              onClick={() => setSelectedKey(path[path.length - 1].id)}
              sx={{ ...CRUMB_SX, ml: 0.75 }}
            >
              Details
            </Button>
          )}
        </Box>
      )}

      {/* The canvas fills what the page leaves, and never less than reads */}
      <Box
        ref={surface}
        sx={{
          position: "relative",
          flex: `1 1 ${CANVAS_HEIGHT}px`,
          minHeight: CANVAS_MIN_HEIGHT,
          overflow: "hidden",
          border: 1,
          borderColor: "divider",
          borderRadius: (theme) => `${theme.radius.surface}px`,
          backgroundColor: "background.paper",
        }}
      >
        {!data || !layout || !index ? (
          <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", p: 3 }}>
            {read.isLoading ? (
              <LogoLoader size="view" surface="paper" label="Loading the treemap" />
            ) : (
              <Typography color="text.secondary">The treemap could not be loaded.</Typography>
            )}
          </Box>
        ) : noClass ? (
          <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", p: 3 }}>
            <Typography color="text.secondary" align="center">
              No class is on record for the {counted(data.evidence.n, "product")} of this
              scope, so there is nothing to map.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ position: "absolute", top: 0, left: 0 }}>
              <TreemapCanvas
                rects={layout.rects}
                width={size.width}
                height={size.height}
                colour={settings.colour}
                selectedKey={selectedKey}
                onSelect={setSelectedKey}
                onDrill={drill}
                onOpen={open}
              />
            </Box>
            {selectedKey && !capturing && (
              <TreemapPanel
                index={index}
                selectedKey={selectedKey}
                rootId={layout.root?.id ?? null}
                gaps={gapsDrawn}
                selection={selectionOf(state)}
                onSelect={setSelectedKey}
                onClear={() => setSelectedKey(null)}
                onDrill={drill}
                onOpenEntity={onOpenEntity}
              />
            )}
          </>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <TreemapLegend measure={settings.measure} colour={settings.colour} gaps={gapsDrawn} />
        <Typography
          component="div"
          color="text.disabled"
          sx={{ fontSize: 12, lineHeight: 1.5 }}
        >
          {data && (
            <span aria-label="Floors">
              {floorsCaption(
                data,
                omittedGaps,
                mapped ? (layout?.hidden ?? 0) : 0,
                undrawnGaps,
              )}
            </span>
          )}
          {mapped && (
            <span> · click a class to drill into it, or to open it where nothing lies beneath</span>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

export default TreemapView;
